// Baselayer Results Explorer — Liens tab. Empty state (with order modal) when
// no Liens add-on exists; otherwise a table of filings, each row linking to the
// lien detail page, plus a "Re-Order Liens" refresh action.

import { useNavigate } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import {
  Button,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  EmptyState,
  StatusBadge,
} from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { useRefreshAddOn } from "@/hooks/backgroundChecks/useAddOns";
import type { BackgroundCheckAddOnDto } from "@/api/backgroundChecks/addOnsApi";
import { AddOnEmptyState } from "../AddOnEmptyState";
import { findAddOn, isCompleted, formatLooseDate } from "../tabHelpers";
import { parseLiens } from "../parse";

interface LiensTabProps {
  checkId: string;
  addOns: BackgroundCheckAddOnDto[] | undefined;
}

export function LiensTab({ checkId, addOns }: LiensTabProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const refresh = useRefreshAddOn(checkId);
  const addOn = findAddOn(addOns, "Liens");

  if (!addOn || !isCompleted(addOn)) {
    return (
      <AddOnEmptyState
        checkId={checkId}
        addOnType="Liens"
        label="Liens"
        title="No liens ordered"
        description="Order a lien search to see UCC filings for this subject."
        addOn={addOn}
      />
    );
  }

  const liens = parseLiens(addOn.rawResponseJson);

  const handleReorder = async () => {
    try {
      await refresh.mutateAsync("Liens");
      toast({ message: "Lien search re-ordered.", tone: "success" });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.errors[0] ?? err.message
          : err instanceof Error
            ? err.message
            : "Could not re-order liens.";
      toast({ message, tone: "error" });
    }
  };

  return (
    <div className="flex flex-col gap-space-3">
      <div className="flex justify-end">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          loading={refresh.isPending}
          disabled={refresh.isPending}
          onClick={handleReorder}
        >
          <RefreshCw size={14} aria-hidden /> Re-Order Liens
        </Button>
      </div>

      {liens.length === 0 ? (
        <EmptyState
          title="No Liens Found"
          description="No UCC lien filings were returned for this subject."
        />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Status</TH>
              <TH>Lien type</TH>
              <TH>Filing date</TH>
              <TH>Lapsed</TH>
              <TH>State</TH>
              <TH>Debtors</TH>
              <TH>Secured party</TH>
            </TR>
          </THead>
          <TBody>
            {liens.map((lien, i) => (
              <TR
                key={lien.id ?? i}
                className="cursor-pointer"
                onClick={() =>
                  lien.id &&
                  navigate(`/background-checks/${checkId}/liens/${lien.id}`)
                }
              >
                <TD>
                  {lien.status ? (
                    <StatusBadge tone="info">{lien.status}</StatusBadge>
                  ) : (
                    "—"
                  )}
                </TD>
                <TD>{lien.filingType ?? "—"}</TD>
                <TD>{formatLooseDate(lien.filingDate)}</TD>
                <TD>{lien.lapsed == null ? "—" : lien.lapsed ? "Yes" : "No"}</TD>
                <TD>{lien.state ?? "—"}</TD>
                <TD>
                  {lien.debtors.map((d) => d.name).filter(Boolean).join(", ") ||
                    "—"}
                </TD>
                <TD>
                  {lien.securedParties
                    .map((s) => s.name)
                    .filter(Boolean)
                    .join(", ") || "—"}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </div>
  );
}
